export type BrowserRuntimeReloadCause =
  | "deployment_refresh_requested"
  | "manual_thread_view_reset"
  | "stale_deployment_recovery"

export interface BrowserRuntimeReloadBreadcrumb {
  cause: BrowserRuntimeReloadCause
  href: string | null
  path: string | null
  recordedAtMs: number
}

const BROWSER_RUNTIME_RELOAD_BREADCRUMB_STORAGE_KEY =
  "cord:browser-runtime-reload-breadcrumb"
/**
 * Keep reload breadcrumbs for one minute so the next browser runtime can explain
 * why hot in-memory thread caches disappeared without letting stale reload causes
 * bleed into unrelated later navigations.
 */
const BROWSER_RUNTIME_RELOAD_BREADCRUMB_TTL_MS = 60_000

function getSessionStorageSafe(): Storage | null {
  if (typeof window === "undefined") return null
  try {
    return window.sessionStorage
  } catch {
    return null
  }
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null
  }
  return value as Record<string, unknown>
}

function asNullableString(value: unknown): string | null {
  if (typeof value !== "string") {
    return null
  }
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

function asPositiveInteger(value: unknown): number | null {
  if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) {
    return null
  }
  return Math.floor(value)
}

function normalizeReloadCause(value: unknown): BrowserRuntimeReloadCause | null {
  if (
    value === "deployment_refresh_requested" ||
    value === "manual_thread_view_reset" ||
    value === "stale_deployment_recovery"
  ) {
    return value
  }
  return null
}

function parseReloadBreadcrumb(value: unknown): BrowserRuntimeReloadBreadcrumb | null {
  const record = asRecord(value)
  if (!record) {
    return null
  }
  const cause = normalizeReloadCause(record.cause)
  const recordedAtMs = asPositiveInteger(record.recordedAtMs)
  if (!cause || recordedAtMs === null) {
    return null
  }
  return {
    cause,
    href: asNullableString(record.href),
    path: asNullableString(record.path),
    recordedAtMs,
  }
}

function removeReloadBreadcrumb(storage: Storage | null): void {
  if (!storage) return
  try {
    storage.removeItem(BROWSER_RUNTIME_RELOAD_BREADCRUMB_STORAGE_KEY)
  } catch {
    // Best effort only.
  }
}

export function writeBrowserRuntimeReloadBreadcrumb(args: {
  cause: BrowserRuntimeReloadCause
  href?: string | null
  path?: string | null
  nowMs?: number
}): void {
  const storage = getSessionStorageSafe()
  if (!storage) return

  const href =
    typeof args.href === "string"
      ? args.href
      : typeof window !== "undefined" && typeof window.location?.href === "string"
        ? window.location.href
        : null
  const path =
    typeof args.path === "string"
      ? args.path
      : typeof window !== "undefined" && typeof window.location?.pathname === "string"
        ? window.location.pathname
        : null

  try {
    storage.setItem(
      BROWSER_RUNTIME_RELOAD_BREADCRUMB_STORAGE_KEY,
      JSON.stringify({
        cause: args.cause,
        href,
        path,
        recordedAtMs: args.nowMs ?? Date.now(),
      }),
    )
  } catch {
    // Best effort only.
  }
}

export function readAndClearBrowserRuntimeReloadBreadcrumb(args?: {
  nowMs?: number
}): BrowserRuntimeReloadBreadcrumb | null {
  const storage = getSessionStorageSafe()
  if (!storage) return null

  try {
    const raw = storage.getItem(BROWSER_RUNTIME_RELOAD_BREADCRUMB_STORAGE_KEY)
    if (!raw) {
      return null
    }
    const parsed = parseReloadBreadcrumb(JSON.parse(raw) as unknown)
    removeReloadBreadcrumb(storage)
    if (!parsed) {
      return null
    }

    const nowMs = args?.nowMs ?? Date.now()
    if (parsed.recordedAtMs + BROWSER_RUNTIME_RELOAD_BREADCRUMB_TTL_MS <= nowMs) {
      return null
    }
    return parsed
  } catch {
    removeReloadBreadcrumb(storage)
    return null
  }
}
